import { WorkfrontValueMatch } from '@atlas/types'
import { Badge, type BadgeProps } from '@atlas/ui-primitives'
import type { MappingRowStatus } from '@/services/workfront-mappings.service'

type Variant = NonNullable<BadgeProps['variant']>

/** What each draft mark reads as: the text carries the status, the colour only repeats it. */
const ROW_STATUS: Record<MappingRowStatus | 'unmapped', { text: string; variant: Variant }> = {
	published: { text: 'in use', variant: 'muted' },
	added: { text: 'draft: added', variant: 'secondary' },
	changed: { text: 'draft change', variant: 'secondary' },
	deleted: { text: 'draft: deleted', variant: 'warning' },
	unmapped: { text: 'unmapped', variant: 'danger' },
}

const DISABLED = { text: 'disabled', variant: 'muted' as const }

/** A row's place against the draft; a disabled field mapping says so instead of `in use`. */
export function RowStatusBadge({
	status,
	enabled = true,
}: {
	status: MappingRowStatus | 'unmapped'
	enabled?: boolean
}) {
	const { text, variant } = status === 'published' && !enabled ? DISABLED : ROW_STATUS[status]
	return (
		<Badge size="sm" variant={variant}>
			{text}
		</Badge>
	)
}

const MATCH: Record<string, Variant> = {
	[WorkfrontValueMatch.EXACT]: 'success',
	[WorkfrontValueMatch.PROPOSED]: 'warning',
	[WorkfrontValueMatch.MANUAL]: 'secondary',
	[WorkfrontValueMatch.UNMATCHED]: 'danger',
}

/** How a label came to be matched: exact, close (proposed), manual or unmatched. */
export function MatchBadge({ match }: { match: string }) {
	return (
		<Badge size="sm" variant={MATCH[match] ?? 'muted'}>
			{match === WorkfrontValueMatch.PROPOSED ? 'close match' : match}
		</Badge>
	)
}
